import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Send, Sparkles, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useEventCart } from "@/context/EventCartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Footer from "@/components/Footer";

export default function RequestQuote() {
  const { items, removeArtist, removeShow, clear, addArtist, addShow } = useEventCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    event_name: "",
    event_date: "",
    city: "",
    venue: "",
    audience_size: "",
    budget: "",
    notes: "",
    contact_name: user && user.name ? user.name : "",
    contact_email: user && user.email ? user.email : "",
    contact_phone: user && user.phone ? user.phone : "",
    organization: user && user.organization ? user.organization : "",
  });
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const runSuggest = async () => {
    if (!form.notes && !form.event_name) {
      toast("Aggiungi qualche dettaglio nelle Note o nel Nome evento");
      return;
    }
    setSuggesting(true);
    setSuggestion(null);
    try {
      const brief = [form.event_name, form.notes].filter(Boolean).join(" — ");
      const { data } = await api.post("/suggest", {
        brief,
        event_date: form.event_date,
        city: form.city,
        audience_size: form.audience_size ? Number(form.audience_size) : null,
        budget: form.budget ? Number(form.budget) : null,
      });
      setSuggestion(data);
      if (!data.artists.length && !data.shows.length) {
        toast("Nessuna proposta trovata — prova con più dettagli");
      } else {
        toast.success("Suggerimenti pronti ✦");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSuggesting(false);
    }
  };

  const acceptAll = () => {
    if (!suggestion) return;
    suggestion.artists.forEach(addArtist);
    suggestion.shows.forEach(addShow);
    toast.success("Aggiunti alla bozza");
    setSuggestion(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        ...form,
        audience_size: form.audience_size ? Number(form.audience_size) : null,
        budget: form.budget ? Number(form.budget) : null,
        artist_ids: items.artists.map((a) => a.id),
        show_ids: items.shows.map((s) => s.id),
      };
      await api.post("/quote-requests", payload);
      toast.success("Richiesta inviata a NMA Agency ✦");
      clear();
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-28 pb-16">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="av-eyebrow">Il mio evento</span>
        <h1 className="font-serif-display text-4xl sm:text-6xl mt-4 leading-tight">
          Componi il tuo evento e chiedi a{" "}
          <span className="italic text-[#E5A93C]">NMA</span> il preventivo.
        </h1>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-10">
          <form onSubmit={submit} className="av-card p-6 lg:p-8 space-y-5" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="av-label">Nome evento *</label>
                <input
                  required
                  className="av-input"
                  value={form.event_name}
                  onChange={upd("event_name")}
                  data-testid="event-name-input"
                />
              </div>
              <div>
                <label className="av-label">Data</label>
                <input
                  type="date"
                  className="av-input"
                  value={form.event_date}
                  onChange={upd("event_date")}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <label className="av-label">Città</label>
                <input
                  className="av-input"
                  value={form.city}
                  onChange={upd("city")}
                  data-testid="event-city-input"
                />
              </div>
              <div>
                <label className="av-label">Luogo / piazza</label>
                <input
                  className="av-input"
                  value={form.venue}
                  onChange={upd("venue")}
                  data-testid="event-venue-input"
                />
              </div>
              <div>
                <label className="av-label">Pubblico atteso</label>
                <input
                  type="number"
                  className="av-input"
                  value={form.audience_size}
                  onChange={upd("audience_size")}
                  data-testid="event-audience-input"
                />
              </div>
              <div>
                <label className="av-label">Budget indicativo (€)</label>
                <input
                  type="number"
                  className="av-input"
                  value={form.budget}
                  onChange={upd("budget")}
                  data-testid="event-budget-input"
                />
              </div>
            </div>
            <div>
              <label className="av-label">Note</label>
              <textarea
                rows={4}
                className="av-input"
                value={form.notes}
                onChange={upd("notes")}
                data-testid="event-notes-input"
                placeholder="Es. Serata di gala all'aperto per il centenario del comune, pubblico misto, apertura elegante e chiusura con concerto."
              />
              <button
                type="button"
                onClick={runSuggest}
                disabled={suggesting}
                className="av-pill av-pill-ghost text-xs mt-3"
                data-testid="ai-suggest-btn"
              >
                <Sparkles size={14} /> {suggesting ? "Sto pensando…" : "Suggerisci artisti e spettacoli con AI"}
              </button>
              {suggestion && (
                <div className="av-card p-4 mt-3" data-testid="ai-suggestion">
                  <div className="av-eyebrow flex items-center gap-2">
                    <Sparkles size={12} /> Proposta NMA · AI
                  </div>
                  {suggestion.rationale && (
                    <p className="text-sm text-[#F7F4EE]/85 mt-2 italic">
                      "{suggestion.rationale}"
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {suggestion.artists.map((a) => (
                      <span key={a.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                        🎤 {a.name}
                      </span>
                    ))}
                    {suggestion.shows.map((s) => (
                      <span key={s.id} className="text-xs px-2 py-1 rounded-full border av-hairline">
                        🎭 {s.title}
                      </span>
                    ))}
                  </div>
                  {(suggestion.artists.length + suggestion.shows.length) > 0 && (
                    <button
                      type="button"
                      onClick={acceptAll}
                      className="av-pill av-pill-gold text-xs mt-4"
                      data-testid="ai-accept-btn"
                    >
                      <Plus size={12} /> Aggiungi tutti alla bozza
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4 border-t av-hairline">
              <div className="av-eyebrow mb-3">Contatto</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="av-label">Nome *</label>
                  <input
                    required
                    className="av-input"
                    value={form.contact_name}
                    onChange={upd("contact_name")}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="av-label">Email *</label>
                  <input
                    required
                    type="email"
                    className="av-input"
                    value={form.contact_email}
                    onChange={upd("contact_email")}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="av-label">Telefono</label>
                  <input
                    className="av-input"
                    value={form.contact_phone}
                    onChange={upd("contact_phone")}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="av-label">Ente / Organizzazione</label>
                  <input
                    className="av-input"
                    value={form.organization}
                    onChange={upd("organization")}
                    data-testid="contact-org-input"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="av-pill av-pill-gold w-full justify-center"
              data-testid="submit-quote-btn"
            >
              <Send size={16} />
              {sending ? "Invio…" : "Richiedi preventivo a NMA Agency ✦"}
            </button>
          </form>

          <aside className="av-card p-6 h-max sticky top-24" data-testid="cart-summary">
            <div className="av-eyebrow">Bozza evento</div>
            <h3 className="font-serif-display text-2xl mt-2">
              {items.artists.length + items.shows.length} elementi selezionati
            </h3>

            <div className="mt-5 space-y-4">
              {items.artists.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3"
                  data-testid={`cart-artist-${a.id}`}
                >
                  <img
                    src={a.image_url}
                    alt={a.name}
                    className="w-12 h-12 object-cover rounded-md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{a.name}</p>
                    <p className="text-[11px] text-[#A39E93]">{a.discipline}</p>
                  </div>
                  <button
                    onClick={() => removeArtist(a.id)}
                    className="text-[#A39E93] hover:text-[#E5A93C]"
                    data-testid={`cart-remove-artist-${a.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {items.shows.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3"
                  data-testid={`cart-show-${s.id}`}
                >
                  <img
                    src={s.image_url}
                    alt={s.title}
                    className="w-12 h-12 object-cover rounded-md"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{s.title}</p>
                    <p className="text-[11px] text-[#A39E93]">{s.category}</p>
                  </div>
                  <button
                    onClick={() => removeShow(s.id)}
                    className="text-[#A39E93] hover:text-[#E5A93C]"
                    data-testid={`cart-remove-show-${s.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {items.artists.length + items.shows.length === 0 && (
                <p className="text-[#A39E93] text-sm">
                  Nessun elemento nella bozza. Aggiungi artisti o spettacoli dal
                  catalogo — oppure invia una richiesta libera compilando solo il form.
                </p>
              )}
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
